import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";

const FormMetadataEditorDialog = ({
  open,
  onClose,
  metadata,
  onMetadataChange,
  formName,
  onFormNameChange,
  onSave,
  loading,
}) => {
  const handleChange = (field, value) => {
    onMetadataChange({
      ...metadata,
      [field]: value,
    });
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md">
      <DialogContent sx={{ padding: "24px", maxWidth: "580px", width: "500px" }}>
        <Typography variant="h5" gutterBottom>
          Edit Form Metadata
        </Typography>

        <Box mt={2}>
          {/* Form Name Field - Separate from metadata */}
          <Box mt={2} mb={2}>
            <TextField
              fullWidth
              margin="dense"
              label="Form Name"
              value={formName || ""}
              onChange={(e) => onFormNameChange(e.target.value)}
              placeholder="Enter the display name for this form"
              helperText="This will be the display name shown in the forms list"
            />
          </Box>

          {/* Short Name Field */}
          <Box mt={2} mb={2}>
            <TextField
              fullWidth
              margin="dense"
              label="Short Name"
              value={metadata.shortName || ""}
              onChange={(e) => handleChange("shortName", e.target.value)}
              disabled // data in DB stored against short key so it can't be enabled
              placeholder="Enter a short name for the onboarding button"
              helperText="Short name can't be upadted, this will used to save data"
            />
          </Box>

          {/* Form Visibility */}
          <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
            <FormLabel component="legend">1. Form Visibility</FormLabel>
            <RadioGroup
              row
              name="visibility"
              value={metadata.visibility || "Both"}
              onChange={(e) => handleChange("visibility", e.target.value)}
            >
              <FormControlLabel value="AdminsOnly" control={<Radio />} label="Admins Only" />
              <FormControlLabel
                value="Both"
                control={<Radio />}
                label="Admins and Applicants Both"
              />
            </RadioGroup>
          </FormControl>

          {/* Onboarding Option */}
          <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
            <FormLabel component="legend">2. Onboarding</FormLabel>
            <RadioGroup
              row
              name="onboarding"
              value={metadata.onboarding || "No"}
              onChange={(e) => handleChange("onboarding", e.target.value)}
            >
              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="No" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>

          {/* Minimum Onboarding Stage - Only show when Onboarding is Yes */}
          {metadata.onboarding === "Yes" && (
            <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
              <FormLabel component="legend">2.1 Minimum Onboarding Stage</FormLabel>
              <RadioGroup
                row
                name="minOnboardingStage"
                value={metadata.minOnboardingStage || "New"}
                onChange={(e) => handleChange("minOnboardingStage", e.target.value)}
              >
                <FormControlLabel value="New" control={<Radio />} label="New" />
                <FormControlLabel value="ATC" control={<Radio />} label="ATC" />
                <FormControlLabel value="Screened" control={<Radio />} label="Screened" />
                <FormControlLabel value="Pre-Hire" control={<Radio />} label="Pre-Hire" />
              </RadioGroup>
            </FormControl>
          )}

          {/* Required Field - Only show when Onboarding is Yes */}
          {metadata.onboarding === "Yes" && (
            <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
              <FormLabel component="legend">2.2 Required at Onboarding</FormLabel>
              <RadioGroup
                row
                name="required"
                value={metadata.required || "No"}
                onChange={(e) => handleChange("required", e.target.value)}
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          )}

          {/* Active/Inactive Status */}
          <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
            <FormLabel component="legend">3. Status</FormLabel>
            <RadioGroup
              row
              name="status"
              value={metadata.status || "Active"}
              onChange={(e) => handleChange("status", e.target.value)}
            >
              <FormControlLabel value="Active" control={<Radio />} label="Active" />
              <FormControlLabel value="Inactive" control={<Radio />} label="Inactive" />
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSave}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
        >
          {loading ? "Saving..." : "Save Metadata"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FormMetadataEditorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  metadata: PropTypes.shape({
    visibility: PropTypes.string,
    onboarding: PropTypes.string,
    minOnboardingStage: PropTypes.string,
    required: PropTypes.string,
    shortName: PropTypes.string,
    status: PropTypes.string,
  }),
  onMetadataChange: PropTypes.func.isRequired,
  formName: PropTypes.string,
  onFormNameChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

FormMetadataEditorDialog.defaultProps = {
  metadata: {
    visibility: "Both",
    onboarding: "No",
    minOnboardingStage: "New",
    required: "No",
    shortName: "",
    status: "Active",
  },
  formName: "",
  loading: false,
};

export default FormMetadataEditorDialog;
